import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useRecoilState } from "recoil"
import { useNavigate, useParams } from "react-router"
import { useAuth } from "src/providers/LoginProvider"
import { Box, Button, Tab, Tabs } from "@mui/material"
import { Layout } from "src/components/layout"
import { SIDE_MENU, SNACKBAR_TYPES, USER_TYPE } from "src/modules/const"
import { Company, CompanyService } from "src/openapi"
import { ShowToast } from "../../states/showToast"
import BreadCrumb from "src/components/atom/BreadCrumb"
import { Top } from "../top"
import { ShowProgress } from "src/states/showProgress"
import { CompanyDeleteModal } from "./CompanyDeleteModal"
import TabPanel from "src/components/atom/TabPanel"
import PermissionTable from "./PermissionTable"

export const CompanyInfo = () => {
  const { t } = useTranslation("translation", { keyPrefix: "company" })

  const navigate = useNavigate()
  const { companyId } = useParams()

  const { user } = useAuth()

  const [tab, setTab] = useState<number>(0)
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue)
  }

  const [company, setCompany] = useState<Company>()

  const [, setShowProgress] = useRecoilState(ShowProgress)
  const [, setShowToast] = useRecoilState(ShowToast)

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)
  const handleDelete = () => {
    if (user == null) {
      return
    }
    setShowProgress(true)

    CompanyService.deleteCompany(user.token, Number(companyId))
      .then(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("success_delete"), type: SNACKBAR_TYPES.SUCCESS })
        navigate("/company")
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_delete"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleGetCompany = () => {
    if (!user) {
      return
    }
    setShowProgress(true)
    CompanyService.getCompany(user.token, Number(companyId))
      .then((res) => {
        setShowProgress(false)
        setCompany(res)
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({
          show: true,
          text: t("failed_get_own_company_info"),
          type: SNACKBAR_TYPES.ERROR,
        })
      })
  }

  const handleCreatePermission = (productName: string) => {
    if (!user) {
      return
    }
    setShowProgress(true)
    CompanyService.createCompanyProductPermission(
      Number(companyId),
      {
        productName: productName,
      },
      user.token
    )
      .then(() => {
        setShowProgress(false)
        setShowToast({
          show: true,
          text: t("success_create_permission"),
          type: SNACKBAR_TYPES.SUCCESS,
        })
        handleGetCompany()
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({
          show: true,
          text: t("failed_create_permission"),
          type: SNACKBAR_TYPES.ERROR,
        })
      })
  }

  const handleDeletePermission = (permissionId: number) => {
    if (!user) {
      return
    }
    setShowProgress(true)
    CompanyService.deleteCompanyProductPermission(user.token, Number(companyId), permissionId)
      .then(() => {
        setShowProgress(false)
        setShowToast({
          show: true,
          text: t("success_delete_permission"),
          type: SNACKBAR_TYPES.SUCCESS,
        })
        handleGetCompany()
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({
          show: true,
          text: t("failed_delete_permission"),
          type: SNACKBAR_TYPES.ERROR,
        })
      })
  }

  useEffect(() => {
    if (user == null) {
      return
    }
    if (user.type != USER_TYPE.ADMIN && user.type != USER_TYPE.GROUPADMIN) {
      return
    }

    handleGetCompany()
  }, [user])

  if (!user) {
    return <Top />
  }

  return (
    <Layout menu={SIDE_MENU.COMPANY_INFO}>
      <BreadCrumb
        paths={[
          {
            name: t("title"),
            link: "",
          },
        ]}
      />
      <Tabs
        className="company-tab"
        value={tab}
        onChange={handleTabChange}
        textColor="secondary"
        indicatorColor="secondary"
      >
        <Tab label={t("company_info")} />
        {user.type == USER_TYPE.ADMIN ? <Tab label={t("product_permission")} /> : <></>}
      </Tabs>
      <TabPanel value={tab} index={0}>
        <Box className="company-content-container">
          <Box className="company-item-container">
            <Box className="company-item-title">{t("employer_id")}</Box>
            <Box className="company-item-content-container">
              <Box className="company-item-content-item">{company?.employerId}</Box>
            </Box>
          </Box>
          <Box className="company-item-container">
            <Box className="company-item-title">{t("company_name")}</Box>
            <Box className="company-item-content-container">
              <Box className="company-item-content-item">{company?.companyName}</Box>
            </Box>
          </Box>
          <Box className="company-item-container">
            <Box className="company-item-title">{t("ceo_name")}</Box>
            <Box className="company-item-content-container">
              <Box className="company-item-content-item">{company?.ceo}</Box>
            </Box>
          </Box>
          <Box className="company-item-container">
            <Box className="company-item-title">{t("post_code")}</Box>
            <Box className="company-item-content-container">
              <Box className="company-item-content-item">
                {company?.postCode ? company?.postCode : <br />}
              </Box>
            </Box>
          </Box>
          <Box className="company-item-container">
            <Box className="company-item-title">{t("address")}</Box>
            <Box className="company-item-content-container">
              <Box className="company-item-content-item">{company?.province}</Box>
              <Box className="company-item-content-item">{company?.city}</Box>
              <Box className="company-item-content-item">{company?.address}</Box>
              <Box className="company-item-content-item">{company?.buildingNo}</Box>
            </Box>
          </Box>
          <Box className="company-item-container">
            <Box className="company-item-title">{t("phone_number")}</Box>
            <Box className="company-item-content-container">
              <Box className="company-item-content-item">
                {company?.phoneNumber ? company?.phoneNumber : <br />}
              </Box>
            </Box>
          </Box>
          <Box className="company-item-container">
            <Box className="company-item-title">{t("mail_address")}</Box>
            <Box className="company-item-content-container">
              <Box className="company-item-content-item">{company?.email}</Box>
            </Box>
          </Box>
        </Box>
        <Button className="update-button" onClick={() => navigate(`/company/${companyId}/update`)}>
          {t("update")}
        </Button>
        {user.type == USER_TYPE.ADMIN ? (
          <>
            <Button
              variant="outlined"
              className="company-delete-button"
              onClick={() => setDeleteModalOpen(true)}
            >
              {t("delete")}
            </Button>
            <CompanyDeleteModal
              open={deleteModalOpen}
              handleOpen={setDeleteModalOpen}
              handleDelete={handleDelete}
            />
          </>
        ) : (
          <></>
        )}
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <PermissionTable
          company={company}
          onCreatePermission={handleCreatePermission}
          onDeletePermission={handleDeletePermission}
        />
      </TabPanel>
    </Layout>
  )
}
